import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Badge, Table, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import apiClient from '../../../api/apiClient';

const InstructorSupportTicketsPage = () => {
  const { user } = useAuth();
  const { refreshNotifications } = useNotifications();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });

  const [createForm, setCreateForm] = useState({
    subject: '',
    description: ''
  });

  const [messageForm, setMessageForm] = useState({
    message: ''
  });

  const [selectedFile, setSelectedFile] = useState(null);

  const statusColors = {
    open: 'primary',
    in_progress: 'info',
    resolved: 'success',
    closed: 'secondary'
  };

  useEffect(() => {
    fetchTickets();
  }, [filters]);

  const fetchTickets = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...filters
      });

      const response = await apiClient.get(`/support-tickets?${params}`);

      if (response.data.success) {
        setTickets(response.data.data.tickets);
        setPagination(response.data.data.pagination);
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Failed to fetch support tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await apiClient.post('/support-tickets', {
        ...createForm,
        is_organization_ticket: false
      });

      if (response.data.success) {
        setSuccess('Support ticket created successfully');
        setShowCreateModal(false);
        setCreateForm({ subject: '', description: '' });
        try {
          await refreshNotifications();
        } catch (notificationError) {
          console.error('Notification refresh failed after ticket creation:', notificationError);
        }
        fetchTickets();
      }
    } catch (err) {
      console.error('Error creating ticket:', err);
      setError(err.response?.data?.message || 'Failed to create support ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleViewTicket = async (ticketId) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/support-tickets/${ticketId}`);

      if (response.data.success) {
        setSelectedTicket(response.data.data);
        setShowTicketModal(true);
        setMessageForm({ message: '' });
        setSelectedFile(null);
      }
    } catch (err) {
      console.error('Error fetching ticket details:', err);
      setError('Failed to fetch ticket details');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageForm.message.trim() && !selectedFile) return;

    try {
      setLoading(true);

      if (messageForm.message.trim()) {
        const messageResponse = await apiClient.post(`/support-tickets/${selectedTicket.id}/messages`, {
          message: messageForm.message.trim()
        });

        if (!messageResponse.data.success) {
          throw new Error('Failed to send message');
        }
      }

      if (selectedFile) {
        const formData = new FormData();
        formData.append('attachment', selectedFile);

        const attachmentResponse = await apiClient.post(`/support-tickets/${selectedTicket.id}/attachments`, formData);

        if (!attachmentResponse.data.success) {
          throw new Error('Failed to upload attachment');
        }
      }

      const refreshResponse = await apiClient.get(`/support-tickets/${selectedTicket.id}`);
      if (refreshResponse.data.success) {
        setSelectedTicket(refreshResponse.data.data);
      }

      setMessageForm({ message: '' });
      setSelectedFile(null);
      setSuccess('Message sent successfully');
      try {
        await refreshNotifications();
      } catch (notificationError) {
        console.error('Notification refresh failed after sending message:', notificationError);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.response?.data?.message || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDownloadAttachment = async (attachmentId) => {
    try {
      await apiClient.supportTickets.downloadAttachment(attachmentId);
    } catch (err) {
      console.error('Download failed:', err);
      setError('Failed to download attachment. Please try again.');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8 lg:p-10">
      <div className="mb-6 sm:mb-8">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl px-5 py-4 sm:p-6 border border-purple-100 shadow-xl">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white text-xl sm:text-2xl shadow-md">
                <i className="fas fa-headset" aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-left text-2xl sm:text-2xl font-bold text-gray-900 mb-1">Support Tickets</h2>
                <p className="text-gray-600 text-sm sm:text-base m-0">
                  Get help from our support team
                </p>
              </div>
            </div>
            <Button
              bsPrefix="custom-hamburger"
              className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 px-3"
              onClick={() => setShowCreateModal(true)}
            >
              <i className="fas fa-plus me-2" aria-hidden="true" />
              Create Ticket
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4 rounded-xl border-2 border-red-200 bg-red-50">
          <div className="flex items-center gap-3">
            <i className="fas fa-exclamation-triangle text-red-600 text-xl" aria-hidden="true" />
            <span className="font-semibold">{error}</span>
          </div>
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')} className="mb-4 rounded-xl border-2 border-emerald-200 bg-emerald-50">
          <div className="flex items-center gap-3">
            <i className="fas fa-check-circle text-emerald-600 text-xl" aria-hidden="true" />
            <span className="font-semibold">{success}</span>
          </div>
        </Alert>
      )}

      <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-xl p-4 sm:p-6 mb-6">
        <Row className="g-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label className="fw-semibold text-gray-700">Status</Form.Label>
              <Form.Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="rounded-lg border-2 border-gray-200 focus:border-purple-500"
              >
                <option value="">All Statuses</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={8}>
            <Form.Group>
              <Form.Label className="fw-semibold text-gray-700">Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search by subject, description, or ticket number..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="rounded-lg border-2 border-gray-200 focus:border-purple-500"
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-xl p-4 sm:p-6">
        {loading ? (
          <div className="text-center py-12 sm:py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-purple-600 border-t-transparent mb-4" />
            <p className="text-gray-600 text-base sm:text-lg font-medium">Loading tickets...</p>
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-12 sm:py-16">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-red-400 to-rose-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <i className="fas fa-ticket-alt text-white text-3xl sm:text-3xl" aria-hidden="true" />
            </div>
            <h5 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">No support tickets found</h5>
            <p className="text-gray-600 text-base sm:text-lg">Create your first support ticket to get help from our team.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table responsive hover className="mb-0">
              <thead>
                <tr className="border-bottom-2">
                  <th className="fw-bold text-gray-700 py-3">Ticket #</th>
                  <th className="fw-bold text-gray-700 py-3">Subject</th>
                  <th className="fw-bold text-gray-700 py-3">Status</th>
                  <th className="fw-bold text-gray-700 py-3">Created</th>
                  <th className="fw-bold text-gray-700 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => (
                  <tr key={ticket.id} className="border-bottom">
                    <td className="py-3">
                      <code className="bg-purple-50 text-purple-700 px-2 py-1 rounded font-semibold">{ticket.ticket_number}</code>
                    </td>
                    <td className="py-3">
                      <div className="fw-bold text-gray-900">{ticket.subject}</div>
                      <small className="text-gray-600">
                        {ticket.description.substring(0, 100)}
                        {ticket.description.length > 100 && '...'}
                      </small>
                    </td>
                    <td className="py-3">
                      <Badge className={`px-3 py-1 rounded-full font-semibold ${
                        ticket.status === 'open' ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white' :
                        ticket.status === 'in_progress' ? 'bg-gradient-to-r from-amber-500 to-yellow-600 text-white' :
                        ticket.status === 'resolved' ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white' :
                        'bg-gradient-to-r from-gray-500 to-gray-600 text-white'
                      }`}>
                        {ticket.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </td>
                    <td className="py-3">
                      <small className="text-gray-600">{formatDate(ticket.createdAt)}</small>
                    </td>
                    <td className="py-3">
                      <Button
                        className="border-0 text-white py-1 px-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 font-semibold"
                        style={{
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                          borderRadius: '8px'
                        }}
                        size="sm"
                        onClick={() => handleViewTicket(ticket.id)}
                      >
                        <i className="fas fa-eye me-1" aria-hidden="true" />
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {pagination.totalPages > 1 && (
          <div className="d-flex justify-content-center mt-6">
            <div className="d-flex align-items-center gap-2">
              <Button
                className="rounded-lg border-0 shadow-sm hover:shadow-md transition-all duration-200 font-semibold"
                style={{
                  backgroundColor: pagination.page === 1 ? '#e5e7eb' : 'white',
                  color: pagination.page === 1 ? '#9ca3af' : '#6366f1',
                  fontWeight: '600'
                }}
                disabled={pagination.page === 1}
                onClick={() => fetchTickets(pagination.page - 1)}
              >
                Previous
              </Button>
              <span className="mx-3 text-gray-700 font-semibold">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <Button
                className="rounded-lg border-0 shadow-sm hover:shadow-md transition-all duration-200 font-semibold"
                style={{
                  backgroundColor: pagination.page === pagination.totalPages ? '#e5e7eb' : 'white',
                  color: pagination.page === pagination.totalPages ? '#9ca3af' : '#6366f1',
                  fontWeight: '600'
                }}
                disabled={pagination.page === pagination.totalPages}
                onClick={() => fetchTickets(pagination.page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4">
          <Modal.Title>Create Support Ticket</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateTicket} className="p-2">
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Subject *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Brief description of your issue"
                value={createForm.subject}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, subject: e.target.value }))}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description *</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                placeholder="Please provide detailed information about your issue..."
                value={createForm.description}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, description: e.target.value }))}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="rounded-b-2xl p-4 border-0">
            <div className="w-100 g-3 row">
              <div className="col-md-6">
                <Button bsPrefix="custom-hamburger" className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2" variant="secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
              </div>
              <div className="col-md-6">
                <Button bsPrefix="custom-hamburger" className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2" variant="success" type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Creating...
                    </>
                  ) : (
                    'Create Ticket'
                  )}
                </Button>
              </div>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      {selectedTicket && (
        <Modal show={showTicketModal} onHide={() => setShowTicketModal(false)} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>
              <code>{selectedTicket.ticket_number}</code> - {selectedTicket.subject}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={12}>
                <Card className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">Ticket Details</h6>
                  </Card.Header>
                  <Card.Body>
                    <p><strong>Description:</strong></p>
                    <p className="text-muted">{selectedTicket.description}</p>

                    <Row className="mt-3">
                      <Col md={6}>
                        <p>
                          <strong>Status:</strong>
                          <Badge bg={statusColors[selectedTicket.status]} className="ms-2">
                            {selectedTicket.status.replace('_', ' ').toUpperCase()}
                          </Badge>
                        </p>
                      </Col>
                      <Col md={6}>
                        <p><strong>Created:</strong> {formatDate(selectedTicket.createdAt)}</p>
                      </Col>
                    </Row>

                    {selectedTicket.resolved_at && (
                      <p><strong>Resolved:</strong> {formatDate(selectedTicket.resolved_at)}</p>
                    )}

                    {selectedTicket.resolution_notes && (
                      <div>
                        <p><strong>Resolution Notes:</strong></p>
                        <p className="text-muted">{selectedTicket.resolution_notes}</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>

                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Conversation ({selectedTicket.messages ? selectedTicket.messages.length : 0} messages)</h6>
                  </Card.Header>
                  <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {selectedTicket.messages && selectedTicket.messages.length > 0 ? (
                      selectedTicket.messages.map((message) => (
                        <div key={message.id} className={`mb-3 ${message.sender.id === user.id ? 'text-end' : ''}`}>
                          <div className={`d-inline-block p-3 rounded ${
                            message.sender.id === user.id
                              ? 'bg-success text-white'
                              : message.sender.role === 0
                                ? 'bg-danger text-white'
                                : message.sender.role === 2
                                  ? 'bg-info text-white'
                                  : 'bg-light'
                          }`} style={{ maxWidth: '70%' }}>
                            <div className="fw-bold mb-1">
                              {message.sender.first_name} {message.sender.last_name}
                              {message.sender.role === 0 && <Badge bg="light" text="dark" className="ms-2">Support</Badge>}
                              {message.sender.role === 2 && <Badge bg="light" text="dark" className="ms-2">Org Admin</Badge>}
                            </div>
                            <p className="mb-1" style={{ whiteSpace: 'pre-wrap' }}>{message.message}</p>
                            <small className={message.sender.id === user.id || message.sender.role === 0 || message.sender.role === 2 ? 'text-light' : 'text-muted'}>
                              {formatDate(message.createdAt)}
                            </small>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4 text-muted">
                        <i className="fas fa-comments fa-2x mb-2" aria-hidden="true" />
                        <p>No messages yet. Start the conversation!</p>
                      </div>
                    )}
                  </Card.Body>

                  {selectedTicket.status !== 'closed' && (
                    <Card.Footer>
                      <Form onSubmit={handleSendMessage}>
                        <div className="d-flex gap-2">
                          <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Type your message..."
                            value={messageForm.message}
                            onChange={(e) => setMessageForm((prev) => ({ ...prev, message: e.target.value }))}
                          />
                          <div className="d-flex flex-column gap-2">
                            <Button type="submit" variant="success" disabled={loading || !messageForm.message.trim()}>
                              <i className="fas fa-paper-plane" aria-hidden="true" />
                            </Button>
                            <Button
                              variant="outline-secondary"
                              onClick={() => document.getElementById('instructor-attachment-input').click()}
                              title="Attach File"
                            >
                              <i className="fas fa-paperclip" aria-hidden="true" />
                            </Button>
                          </div>
                        </div>
                        <input
                          id="instructor-attachment-input"
                          type="file"
                          style={{ display: 'none' }}
                          onChange={handleFileSelect}
                          accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt"
                        />
                        {selectedFile && (
                          <div className="mt-2 p-2 bg-light rounded d-flex justify-content-between align-items-center">
                            <span>
                              <i className="fas fa-file me-2" aria-hidden="true" />
                              {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                            </span>
                            <Button variant="outline-danger" size="sm" onClick={() => setSelectedFile(null)}>
                              <i className="fas fa-times" aria-hidden="true" />
                            </Button>
                          </div>
                        )}
                      </Form>
                    </Card.Footer>
                  )}
                </Card>
              </Col>

              <Col md={4}>
                {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Attachments</h6>
                    </Card.Header>
                    <Card.Body>
                      {selectedTicket.attachments.map((attachment) => (
                        <div key={attachment.id} className="d-flex align-items-center mb-2">
                          <i className={`fas ${attachment.is_image ? 'fa-image' : 'fa-file'} me-2`} aria-hidden="true" />
                          <div className="flex-grow-1">
                            <div className="fw-bold">{attachment.original_filename}</div>
                            <small className="text-muted">
                              {(attachment.file_size / 1024).toFixed(1)} KB
                            </small>
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleDownloadAttachment(attachment.id)}
                          >
                            <i className="fas fa-download" aria-hidden="true" />
                          </Button>
                        </div>
                      ))}
                    </Card.Body>
                  </Card>
                )}
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowTicketModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default InstructorSupportTicketsPage;
